import filter from 'lodash-es/filter'
import type { KeyboardEvent, ReactNode } from 'react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDebounce } from 'use-debounce'

import { Icon } from '@fourthwall/components'
import { ResetIcon, SearchIcon } from '@fourthwall/icons'

import type {
  Mode,
  ProductsStateResolver,
  SelectMode,
  SelectOffersSource,
} from './SelectProductsModal.types'
import { SelectProductsModalView } from './SelectProductsModalView'
import { SEARCH_MIN_LENGTH } from './consts'
import type { useProductsInfiniteQuery } from './hooks/useProductsInfiniteQuery'
import { useUnifiedInfiniteOffers } from './hooks/useUnifiedInfiniteOffers'
import { getItemsFromProducts } from './utils/getItemsFromProducts'
import { getProductsStates } from './utils/getProductStates'

export type SelectProductsContainerProps = {
  source?: SelectOffersSource
  mode?: Mode
  selectMode?: SelectMode
  multiple?: boolean
  title?: string
  selectedIds?: string[]
  productsStateResolver?: ProductsStateResolver
  emptyMessage?: ReactNode
  initialInputValue?: string
  submitButtonLabel?: string
  cancelButtonLabel?: string
  includeMembershipProducts?: boolean
  params?: Parameters<typeof useProductsInfiniteQuery>[2] | undefined
  onSave: (selectedProductsIds: string[]) => void
  close: () => void
}

export const SelectProductsModalContainer = ({
  multiple = true,
  selectMode = 'OFFERS',
  source = 'offers',
  title,
  params,
  mode = 'COMMON',
  selectedIds = [],
  productsStateResolver,
  initialInputValue = '',
  submitButtonLabel = 'Save',
  cancelButtonLabel = 'Cancel',
  emptyMessage = 'There are no products to select.',
  onSave,
  close,
}: SelectProductsContainerProps) => {
  const [inputSuggestion, setInputSuggestion] = useState<string | undefined>()
  const [inputValue, setInputValue] = useState(initialInputValue)
  const [inputValueDebounced] = useDebounce(inputValue, 300)
  const [innerIds, setInnerIds] = useState(selectedIds)

  const { products, hasNextPage, isLoading, fetchNextPage } = useUnifiedInfiniteOffers(
    source,
    inputValueDebounced,
    params,
  )

  const items = useMemo(() => getItemsFromProducts(products, selectMode), [products, selectMode])
  const productsStates = useMemo(
    () => getProductsStates(productsStateResolver, products),
    [productsStateResolver, products],
  )

  const resetInputValue = useCallback(() => setInputValue(''), [])

  useEffect(() => {
    setInputSuggestion(undefined)
  }, [inputValue])

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (inputValue.length && inputValue.length < SEARCH_MIN_LENGTH && event.key === 'Enter') {
        setInputSuggestion(`Enter at least ${SEARCH_MIN_LENGTH} characters to start the search.`)
      }
    },
    [inputValue],
  )

  const handleLoadMore = useCallback(() => {
    if (hasNextPage) {
      fetchNextPage()
    }
  }, [hasNextPage, fetchNextPage])

  const handleSelect = useCallback(
    (id: string) => (value: boolean) => {
      if (!multiple) {
        setInnerIds([id])

        return
      }

      setInnerIds((prev) => (value ? [id, ...prev] : filter(prev, (itemId) => itemId !== id)))
    },
    [multiple],
  )

  const handleBulkSelect = useCallback(
    (ids: string[]) => (value: boolean) => {
      if (!multiple) return

      setInnerIds((prev) =>
        value ? [...prev, ...ids] : filter(prev, (itemId) => !ids.includes(itemId)),
      )
    },
    [multiple],
  )

  const handleSubmitClick = useCallback(() => {
    onSave?.(innerIds)
    close()
  }, [innerIds, onSave, close])

  const message = useMemo(() => {
    if (!multiple) {
      if (!innerIds.length) return 'No product selected'
      const productName = items?.find((item) => item.id === innerIds[0])?.name

      return `${productName} selected`
    }

    const productsCount = innerIds.length

    return `${productsCount} ${productsCount === 1 ? 'product' : 'products'} selected`
  }, [multiple, innerIds, items])

  const emptyStateMessage = useMemo(() => {
    if (!products?.length) {
      return !inputValueDebounced.length ? emptyMessage : 'No products found.'
    }

    return undefined
  }, [products, inputValueDebounced, emptyMessage])

  const inputProps = useMemo(
    () => ({
      value: inputValue,
      placeholder: 'Search products',
      leftIcon: SearchIcon,
      disabled: false,
      actionIcon: inputValue.length ? <Icon component={ResetIcon} height={18} /> : undefined,
      suggestion: inputSuggestion,
      onKeyDown: handleKeyDown,
      onValueChange: setInputValue,
      onActionIconClick: resetInputValue,
    }),
    [inputValue, inputSuggestion, handleKeyDown, resetInputValue],
  )

  return (
    <SelectProductsModalView
      title={title ?? `Select product${multiple ? 's' : ''}`}
      items={items}
      productsStates={productsStates}
      innerIds={innerIds}
      multiple={multiple}
      mode={mode}
      selectMode={selectMode}
      message={message}
      emptyMessage={emptyStateMessage}
      isLoading={isLoading}
      hasNextPage={!!hasNextPage}
      submitButtonLabel={submitButtonLabel}
      cancelButtonLabel={cancelButtonLabel}
      inputProps={inputProps}
      close={close}
      onLoadMore={handleLoadMore}
      onSelect={handleSelect}
      onBulkSelect={handleBulkSelect}
      onSubmitClick={handleSubmitClick}
    />
  )
}
